import React from 'react';
import Dialog from 'pages/Dashboard/components/Dialog';
import noop from 'lodash/noop';
import {
  useNotify,
} from 'core/hooks';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import Button from 'pages/Dashboard/components/Button';
import Controller from 'core/components/FormController';
import Input from 'pages/Dashboard/components/Input';
import {
  notEmptyString,
} from '@populateui/utils';
import isNil from 'lodash/isNil';
import {
  FormCategoryDTO,
} from 'dtos';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useCopyForm,
} from 'views/EMR/Settings/PdfTemplateBuilder/hooks';

type FormCopyData = {
  title: string;
};

export type CopyDialogProps = {
  sourceFormId: number;
  sourceTitle: string;
  sourceCategory?: FormCategoryDTO | null;
  onClose: () => void;
  onOpenBuilder: (formId: number) => void;
};

export default function CopyDialog({
  sourceFormId,
  sourceTitle,
  sourceCategory,
  onClose,
  onOpenBuilder,
}: CopyDialogProps) {
  const formProps = useForm<FormCopyData>({
    mode: 'onChange',
    defaultValues: {
      title: `Copy of ${sourceTitle}`,
    },
  });
  const {
    control,
    handleSubmit,
    reset,
  } = formProps;

  const notify = useNotify();
  const {
    mutateAsync: copyForm,
    isPending: isCopying,
  } = useCopyForm();

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async ({ title }: FormCopyData) => {
    try {
      const copiedForm = await copyForm({
        sourceFormId,
        title: title.trim(),
      });
      const { formId } = copiedForm;

      if (isNil(formId)) {
        throw new Error('Missing form id');
      }

      notify({
        variant: 'success',
        message: 'Form was copied successfully',
      });
      settingsEventChannel.emit('onChangeTemplates');
      onClose();
      onOpenBuilder(formId);
    } catch {
      notify({
        variant: 'error',
        message: 'Failed to copy form',
      });
    }
  };

  return (
    <FormProvider {...formProps}>
      <Dialog
        title="Copy Form"
        onClose={handleClose}
        onConfirm={noop}
        open
      >
        <Controller
          name="title"
          control={control}
          rules={{
            validate: (value) => notEmptyString(value) || 'Title is required',
          }}
          render={({ field: { value, ...field }, fieldState: { invalid, isTouched } }) => (
            <Input
              {...field}
              value={typeof value === 'string' ? value : ''}
              error={isTouched && invalid}
              className="text-gray-500 text-xs"
              id="copy-form-title"
              placeholder="Enter Form Title"
              label="Title"
              required
              inputProps={{ maxLength: 64 }}
            />
          )}
        />
        <Input
          value={sourceCategory?.name ?? '--'}
          className="text-gray-500 text-xs"
          id="copy-form-category"
          label="Category"
          disabled
        />
        <div className="flex gap-2 justify-end">
          <Button
            variant="outlined"
            type="button"
            onClick={handleClose}
            disabled={isCopying}
          >
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="button"
            onClick={handleSubmit(onSubmit)}
            disabled={isCopying}
          >
            {isCopying ? 'Copying...' : 'Copy'}
          </Button>
        </div>
      </Dialog>
    </FormProvider>
  );
}
